<template>
  <div class="max-w-3xl w-full mx-auto space-y-6">
    <h1 class="text-2xl font-bold text-gray-800">Choose an admin action</h1>
    <div class="grid md:grid-cols-2 gap-6">
      <div class="border rounded-lg p-6 bg-white shadow-sm flex flex-col">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2 6a2 2 0 012-2h10a2 2 0 012 2v1.382l3.447-1.724A1 1 0 0121 6.553v6.894a1 1 0 01-1.553.895L16 12.618V14a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"/>
          </svg>
          <h2 class="text-lg font-semibold">Clip Source Upload</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">Upload a source video for a ceremony.</p>
        <router-link
          class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          to="/admin/upload"
        >
          Go to Clip Upload
        </router-link>
      </div>

      <div class="border rounded-lg p-6 bg-white shadow-sm flex flex-col" :class="{ 'opacity-50': !canSnapshotUpload }">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M4 5a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V7a2 2 0 00-2-2H4zm3 3l3 4 2-2 4 5H6l1-7z"/>
          </svg>
          <h2 class="text-lg font-semibold">Snapshot Bulk Upload</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">Upload multiple images for a ceremony using presigned URLs.</p>
        <div v-if="!canSnapshotUpload" class="text-xs text-red-600 mb-3">
          You don’t have the snapshot-upload role.
        </div>
        <router-link
          v-if="canSnapshotUpload"
          class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          to="/admin/snapshot-bulk"
        >
          Go to Snapshot Uploader
        </router-link>
        <button v-else disabled class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed">
          Access restricted
        </button>
      </div>

      <div class="border rounded-lg p-6 bg-white shadow-sm flex flex-col" :class="{ 'opacity-50': !canViewOrders }">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5 9a1 1 0 011-1h2v10H6a1 1 0 01-1-1V9zm6-4a1 1 0 011-1h2v14h-2a1 1 0 01-1-1V5zm6 7a1 1 0 011-1h2v7h-2a1 1 0 01-1-1v-5z"/>
          </svg>
          <h2 class="text-lg font-semibold">View Order Analytics</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">View order analytics and insights for retail products.</p>
        <div v-if="!canViewOrders" class="text-xs text-red-600 mb-3">
          You don’t have the required role (admin, orders:read, analytics:read).
        </div>
        <router-link
          v-if="canViewOrders"
          class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          to="/admin/order-analytics"
        >
          Open Order Analytics
        </router-link>
        <button v-else disabled class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed">
          Access restricted
        </button>
      </div>
      <div class="border rounded-lg p-6 bg-white shadow-sm flex flex-col" :class="{ 'opacity-50': !canEditShare }">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 2L11 13"/>
            <path d="M22 2L15 22l-4-9-9-4z"/>
          </svg>

          <h2 class="text-lg font-semibold">Share Message Presets</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">Configure default share text per platform for each university.</p>
        <div v-if="!canEditShare" class="text-xs text-red-600 mb-3">
          You don’t have the required role (admin, university-admin, or social-media).
        </div>
        <router-link
          v-if="canEditShare"
          class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          to="/admin/share-templates"
        >
          Open Share Presets
        </router-link>
        <button v-else disabled class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed">
          Access restricted
        </button>
      </div>
    </div>
  </div>
</template>

<script>
function decodeJwt(token) {
  try {
    const part = token.split('.')[1]
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decodeURIComponent(escape(json)))
  } catch (e) {
    return {}
  }
}

export default {
  name: 'ChooseAction',
  created() {
    // If the user doesn't have snapshot-upload role, auto-redirect to clip upload
    if (!this.canSnapshotUpload) {
      this.$router.replace('/admin/upload')
    }
  },
  computed: {
    canSnapshotUpload() {
      const token = localStorage.getItem('token') || ''
      const payload = decodeJwt(token)
      const roles = payload?.roles || []
      return Array.isArray(roles) && (roles.includes('snapshot-upload') || roles.includes('admin'))
    }
    ,
    canEditShare() {
      const token = localStorage.getItem('token') || ''
      const payload = decodeJwt(token)
      const roles = payload?.roles || []
      return Array.isArray(roles) && (roles.includes('admin') || roles.includes('university-admin') || roles.includes('social-media'))
    },
    canViewOrders() {
      const token = localStorage.getItem('token') || ''
      const payload = decodeJwt(token)
      const roles = payload?.roles || []
      return Array.isArray(roles) && (roles.includes('orders:read') || roles.includes("analytics:read") || roles.includes('admin'))
    }
  }
}
</script>

<style scoped>
</style>
